import { cn } from '../../../lib/cn';

interface Props {
    name?: string | null;
    email?: string | null;
    size?: 'sm' | 'md';
    className?: string;
}

// 8-color palette with paired text colors that meet WCAG AA on the bg.
// Picked from Tailwind's "soft" tints so they sit alongside the muted
// modal chrome without shouting.
const PALETTE = [
    'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-200',
    'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200',
    'bg-lime-100 text-lime-700 dark:bg-lime-900/40 dark:text-lime-200',
    'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200',
    'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-200',
    'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-200',
    'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-200',
    'bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-900/40 dark:text-fuchsia-200',
];

function hashIndex(seed: string): number {
    let h = 2166136261;
    for (let i = 0; i < seed.length; i++) {
        h ^= seed.charCodeAt(i);
        h = Math.imul(h, 16777619);
    }
    return Math.abs(h) % PALETTE.length;
}

function initials(name?: string | null, email?: string | null): string {
    const src = (name || email || '?').trim();
    if (!src) return '?';
    const parts = src.split(/[\s@._-]+/).filter(Boolean);
    if (parts.length === 0) return src[0]!.toUpperCase();
    const first = parts[0]![0] ?? '';
    const second = parts.length > 1 ? parts[1]![0] : '';
    return (first + second).toUpperCase().slice(0, 2);
}

export function Avatar({ name, email, size = 'md', className }: Props) {
    const seed = (name || email || 'anon').toLowerCase();
    const palette = PALETTE[hashIndex(seed)];
    const dimensions = size === 'sm' ? 'h-6 w-6 text-[10px]' : 'h-8 w-8 text-xs';

    return (
        <span
            aria-hidden="true"
            className={cn(
                'inline-flex shrink-0 items-center justify-center rounded-full font-semibold',
                dimensions,
                palette,
                className,
            )}
        >
            {initials(name, email)}
        </span>
    );
}
